import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import {
  Plus,
  Trash2,
  GripVertical,
  Settings,
  Users,
  ToggleLeft,
  Tag,
  Layers,
  Save,
  RefreshCw,
  Boxes,
  LayoutGrid,
  FileText,
  Mail,
  User,
  Crown,
} from 'lucide-react';
import clsx from 'clsx';

const STATE_COLORS = [
  '#6b7280', '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899',
];

const STATE_GROUPS = [
  { value: 1, label: 'Backlog' },
  { value: 2, label: 'Unstarted' },
  { value: 3, label: 'Started' },
  { value: 4, label: 'Completed' },
  { value: 5, label: 'Cancelled' },
];

const TABS = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'features', label: 'Features', icon: ToggleLeft },
  { id: 'states', label: 'States', icon: Layers },
  { id: 'labels', label: 'Labels', icon: Tag },
];

const getGroupLabel = (group: number) => {
  const found = STATE_GROUPS.find(g => g.value === group);
  return found?.label || 'Unknown';
};

export function SettingsPage() {
  const { workspaceId, projectId } = useParams<{ workspaceId: string; projectId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('general');

  // General settings state
  const [projectName, setProjectName] = useState('');
  const [projectIdentifier, setProjectIdentifier] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [hasGeneralChanges, setHasGeneralChanges] = useState(false);

  // Features state
  const [features, setFeatures] = useState({
    cycles: true,
    modules: true,
    views: true,
    pages: false,
    inbox: false,
  });

  // States
  const [isCreatingState, setIsCreatingState] = useState(false);
  const [newState, setNewState] = useState({ name: '', color: STATE_COLORS[0], group: 1 });

  // Labels
  const [isCreatingLabel, setIsCreatingLabel] = useState(false);
  const [newLabel, setNewLabel] = useState({ name: '', color: STATE_COLORS[0] });

  // Queries
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId!),
    enabled: !!projectId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId!),
    enabled: !!projectId,
  });

  const { data: labels } = useQuery({
    queryKey: ['labels', projectId],
    queryFn: () => api.getLabels(projectId!),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.getMembers(workspaceId!),
    enabled: !!workspaceId,
  });

  // Initialize general settings when project loads
  useState(() => {
    if (project) {
      setProjectName(project.bh_name || '');
      setProjectIdentifier(project.bh_identifier || '');
      setProjectDescription(project.bh_description || '');
    }
  });

  // Mutations
  const updateProjectMutation = useMutation({
    mutationFn: (data: any) => api.updateProject(projectId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setHasGeneralChanges(false);
    },
  });

  const createStateMutation = useMutation({
    mutationFn: (data: any) => api.createState(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['states', projectId] });
      setIsCreatingState(false);
      setNewState({ name: '', color: STATE_COLORS[0], group: 1 });
    },
  });

  const deleteStateMutation = useMutation({
    mutationFn: (id: string) => api.deleteState(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['states', projectId] });
    },
  });

  const createLabelMutation = useMutation({
    mutationFn: (data: any) => api.createLabel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labels', projectId] });
      setIsCreatingLabel(false);
      setNewLabel({ name: '', color: STATE_COLORS[0] });
    },
  });

  const deleteLabelMutation = useMutation({
    mutationFn: (id: string) => api.deleteLabel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labels', projectId] });
    },
  });

  // Handlers
  const handleSaveGeneral = () => {
    updateProjectMutation.mutate({
      bh_name: projectName,
      bh_identifier: projectIdentifier.toUpperCase(),
      bh_description: projectDescription,
    });
  };

  const handleCreateState = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newState.name.trim()) return;

    createStateMutation.mutate({
      bh_name: newState.name.trim(),
      bh_color: newState.color,
      bh_group: newState.group,
      bh_sequence: (states?.length || 0) + 1,
      bh_project: projectId,
    });
  };

  const handleCreateLabel = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLabel.name.trim()) return;

    createLabelMutation.mutate({
      bh_name: newLabel.name.trim(),
      bh_color: newLabel.color,
      bh_project: projectId,
    });
  };

  const handleFeatureToggle = (feature: keyof typeof features) => {
    setFeatures(prev => ({ ...prev, [feature]: !prev[feature] }));
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>
              <p className="text-sm text-gray-500 mb-6">
                Manage your project's basic information and settings.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name
                </label>
                <input
                  type="text"
                  value={projectName || project?.bh_name || ''}
                  onChange={(e) => {
                    setProjectName(e.target.value);
                    setHasGeneralChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter project name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Identifier
                </label>
                <input
                  type="text"
                  value={projectIdentifier || project?.bh_identifier || ''}
                  onChange={(e) => {
                    setProjectIdentifier(e.target.value.toUpperCase().slice(0, 5));
                    setHasGeneralChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 uppercase"
                  placeholder="e.g., PMR"
                  maxLength={5}
                />
                <p className="text-xs text-gray-500 mt-1">
                  A unique identifier used for issue IDs (max 5 characters)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={projectDescription || project?.bh_description || ''}
                  onChange={(e) => {
                    setProjectDescription(e.target.value);
                    setHasGeneralChanges(true);
                  }}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="Describe your project..."
                />
              </div>

              <div className="pt-4">
                <button
                  onClick={handleSaveGeneral}
                  disabled={!hasGeneralChanges || updateProjectMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {updateProjectMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h4 className="text-lg font-medium text-red-600 mb-2">Danger Zone</h4>
              <p className="text-sm text-gray-500 mb-4">
                Once you delete a project, there is no going back. Please be certain.
              </p>
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
                    // Would call delete API here
                    alert('Project deletion would happen here');
                  }
                }}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
              >
                Delete Project
              </button>
            </div>
          </div>
        );

      case 'members':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Project Members</h3>
              <p className="text-sm text-gray-500 mb-6">
                Manage who has access to this project and their roles.
              </p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  {members?.length || 0} members
                </span>
                <button className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
                  <Plus className="w-4 h-4" />
                  Add Member
                </button>
              </div>

              <div className="divide-y divide-gray-100">
                {members?.map((member: any, index: number) => (
                  <div
                    key={member.bh_memberid}
                    className="px-4 py-3 flex items-center gap-4"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{member.bh_name}</p>
                      <p className="text-xs text-gray-500">{member.bh_email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {index === 0 ? (
                        <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                          <Crown className="w-3 h-3" />
                          Owner
                        </span>
                      ) : (
                        <select className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500">
                          <option value="admin">Admin</option>
                          <option value="member" selected>Member</option>
                          <option value="guest">Guest</option>
                        </select>
                      )}
                      {index !== 0 && (
                        <button className="p-1 text-gray-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {(!members || members.length === 0) && (
                  <div className="px-4 py-8 text-center text-gray-500">
                    No members found in this workspace.
                  </div>
                )}
              </div>
            </div>

            {/* Invite Section */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Invite by Email
              </h4>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter email address"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="guest">Guest</option>
                </select>
                <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Invite
                </button>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Project Features</h3>
              <p className="text-sm text-gray-500 mb-6">
                Enable or disable features for this project. Disabled features will be hidden from the sidebar.
              </p>
            </div>

            <div className="space-y-4">
              {/* Cycles */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Cycles</p>
                    <p className="text-sm text-gray-500">Time-boxed sprints for agile workflows</p>
                  </div>
                </div>
                <button
                  onClick={() => handleFeatureToggle('cycles')}
                  className={clsx(
                    'relative w-12 h-6 rounded-full transition-colors',
                    features.cycles ? 'bg-primary-600' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                      features.cycles ? 'translate-x-6' : 'translate-x-0.5'
                    )}
                  />
                </button>
              </div>

              {/* Modules */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Boxes className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Modules</p>
                    <p className="text-sm text-gray-500">Group related features and deliverables</p>
                  </div>
                </div>
                <button
                  onClick={() => handleFeatureToggle('modules')}
                  className={clsx(
                    'relative w-12 h-6 rounded-full transition-colors',
                    features.modules ? 'bg-primary-600' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                      features.modules ? 'translate-x-6' : 'translate-x-0.5'
                    )}
                  />
                </button>
              </div>

              {/* Views */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <LayoutGrid className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Views</p>
                    <p className="text-sm text-gray-500">Board, List, and Gantt chart views</p>
                  </div>
                </div>
                <button
                  onClick={() => handleFeatureToggle('views')}
                  className={clsx(
                    'relative w-12 h-6 rounded-full transition-colors',
                    features.views ? 'bg-primary-600' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                      features.views ? 'translate-x-6' : 'translate-x-0.5'
                    )}
                  />
                </button>
              </div>

              {/* Pages */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Pages</p>
                    <p className="text-sm text-gray-500">Documentation and wiki pages</p>
                  </div>
                </div>
                <button
                  onClick={() => handleFeatureToggle('pages')}
                  className={clsx(
                    'relative w-12 h-6 rounded-full transition-colors',
                    features.pages ? 'bg-primary-600' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                      features.pages ? 'translate-x-6' : 'translate-x-0.5'
                    )}
                  />
                </button>
              </div>

              {/* Inbox */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Inbox</p>
                    <p className="text-sm text-gray-500">Triage incoming requests and issues</p>
                  </div>
                </div>
                <button
                  onClick={() => handleFeatureToggle('inbox')}
                  className={clsx(
                    'relative w-12 h-6 rounded-full transition-colors',
                    features.inbox ? 'bg-primary-600' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={clsx(
                      'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                      features.inbox ? 'translate-x-6' : 'translate-x-0.5'
                    )}
                  />
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              Note: Feature toggles are stored locally in this demo. In production, these would persist to the database.
            </p>
          </div>
        );

      case 'states':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Workflow States</h3>
                <p className="text-sm text-gray-500">
                  Define the stages that issues move through in your workflow.
                </p>
              </div>
              <button
                onClick={() => setIsCreatingState(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Add State
              </button>
            </div>

            {isCreatingState && (
              <form onSubmit={handleCreateState} className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-3 gap-4">
                  <input
                    type="text"
                    value={newState.name}
                    onChange={(e) => setNewState({ ...newState, name: e.target.value })}
                    placeholder="State name"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                  <select
                    value={newState.group}
                    onChange={(e) => setNewState({ ...newState, group: parseInt(e.target.value) })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {STATE_GROUPS.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                  <div className="flex gap-1 items-center">
                    {STATE_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewState({ ...newState, color })}
                        className={`w-6 h-6 rounded-full border-2 ${
                          newState.color === color ? 'border-gray-800 scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={createStateMutation.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreatingState(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="bg-white rounded-lg border border-gray-200">
              {states?.map((state: any) => (
                <div
                  key={state.bh_stateid}
                  className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0 group"
                >
                  <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: state.bh_color }}
                  />
                  <span className="flex-1 text-sm font-medium text-gray-900">
                    {state.bh_name}
                  </span>
                  <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                    {getGroupLabel(state.bh_group)}
                  </span>
                  <button
                    onClick={() => {
                      if (confirm('Delete this state?')) {
                        deleteStateMutation.mutate(state.bh_stateid);
                      }
                    }}
                    className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {states?.length === 0 && (
                <div className="px-4 py-8 text-center text-gray-500">
                  No states yet. Add some to organize your workflow.
                </div>
              )}
            </div>
          </div>
        );

      case 'labels':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">Labels</h3>
                <p className="text-sm text-gray-500">
                  Create labels to categorize and filter your issues.
                </p>
              </div>
              <button
                onClick={() => setIsCreatingLabel(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Add Label
              </button>
            </div>

            {isCreatingLabel && (
              <form onSubmit={handleCreateLabel} className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={newLabel.name}
                    onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })}
                    placeholder="Label name"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                  <div className="flex gap-1 items-center">
                    {STATE_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewLabel({ ...newLabel, color })}
                        className={`w-6 h-6 rounded-full border-2 ${
                          newLabel.color === color ? 'border-gray-800 scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={createLabelMutation.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreatingLabel(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex flex-wrap gap-2">
                {labels?.map((label: any) => (
                  <div
                    key={label.bh_labelid}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white group"
                    style={{ backgroundColor: label.bh_color }}
                  >
                    {label.bh_name}
                    <button
                      onClick={() => {
                        if (confirm('Delete this label?')) {
                          deleteLabelMutation.mutate(label.bh_labelid);
                        }
                      }}
                      className="opacity-0 group-hover:opacity-100 hover:bg-white/20 rounded-full p-0.5 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                {labels?.length === 0 && (
                  <div className="w-full py-8 text-center text-gray-500">
                    No labels yet. Add some to categorize your issues.
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div className="w-64 border-r border-gray-200 bg-white p-4">
        <h1 className="text-lg font-semibold text-gray-900 mb-6">Project Settings</h1>
        <nav className="space-y-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left',
                  activeTab === tab.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-3xl">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
